/**
 * Create Tenant Modal
 * Form to create a new tenant manually
 */

import React, { useState, useEffect } from 'react';
import { tenantsApi, panelApi } from '../utils/api';
import { XMarkIcon } from '@heroicons/react/24/outline';
import useEscClose from '../../hooks/useEscClose';

export default function CreateTenantModal({ onClose, onCreated }) {
  useEscClose(onClose);
  const [planes, setPlanes] = useState([]);
  useEffect(() => { panelApi.planes().then(r => setPlanes((r.data.planes || []).filter(x => x.activo))).catch((err) => { console.error('[components/CreateTenantModal.jsx]:', err?.message); }); }, []);

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    plan: 'essentials',
    contact_email: '',
    contact_phone: '',
    notes: '',
    max_users: 3,
    max_emisores: 1,
    admin_email: '',
    admin_password: '',
    admin_name: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData({
      ...formData,
      name,
      slug: generateSlug(name)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await tenantsApi.create(formData);
      onCreated();
    } catch (err) {
      // Sin respuesta (se agotó la espera o se cortó la red) no se sabe si la cuenta quedó creada:
      // el servidor pudo terminarla igual. Reintentar sin mirar la lista la duplicaría o chocaría.
      setError(err.response?.data?.error || (err.response
        ? 'Error al crear tenant'
        : 'No llegó la respuesta del servidor. La cuenta pudo haberse creado: actualice la lista de Cuentas antes de intentar de nuevo.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />

      <div className="relative w-full max-w-lg bg-white border border-gray-200 rounded-xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Crear Nuevo Tenant</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-900">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-500/50 rounded-lg p-3 text-red-600 text-sm">
              {error}
            </div>
          )}

          {/* Basic Info */}
          <div>
            <h3 className="text-gray-900 font-medium mb-3">Información Básica</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-500 mb-1">Nombre *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={handleNameChange}
                  className="w-full bg-gray-100 border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Mi Empresa S.A."
                  required
                />
              </div>

              <div>
                <label className="block text-sm text-gray-500 mb-1">Slug *</label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: generateSlug(e.target.value) })}
                  className="w-full bg-gray-100 border border-gray-300 rounded-lg px-3 py-2 text-gray-900 font-mono focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="mi-empresa"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  URL: finance360.com/<span className="text-gray-500">{formData.slug || 'slug'}</span>
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-gray-500 mb-1">Plan</label>
                  <select
                    value={formData.plan}
                    onChange={(e) => {
                      const pl = planes.find(x => x.codigo === e.target.value);
                      setFormData({ ...formData, plan: e.target.value, ...(pl ? { max_users: pl.maxUsuarios, max_emisores: pl.maxEmpresas } : {}) });
                    }}
                    className="w-full bg-gray-100 border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {planes.map(x => <option key={x.codigo} value={x.codigo}>{x.nombre} · US${x.precioMensual}/mes</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-gray-500 mb-1">Max Usuarios</label>
                  <input
                    type="number"
                    value={formData.max_users}
                    onChange={(e) => setFormData({ ...formData, max_users: parseInt(e.target.value) })}
                    className="w-full bg-gray-100 border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min={1}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-gray-900 font-medium mb-3">Contacto</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-gray-500 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  className="w-full bg-gray-100 border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="contacto@empresa.com"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-500 mb-1">Teléfono</label>
                <input
                  type="text"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                  className="w-full bg-gray-100 border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="+506 8888-8888"
                />
              </div>
            </div>
          </div>

          {/* Admin User */}
          <div>
            <h3 className="text-gray-900 font-medium mb-3">Usuario Administrador</h3>
            <p className="text-xs text-gray-500 mb-3">
              Opcional. Si no se proporciona, se deberá crear manualmente.
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-500 mb-1">Nombre del Admin</label>
                <input
                  type="text"
                  value={formData.admin_name}
                  onChange={(e) => setFormData({ ...formData, admin_name: e.target.value })}
                  className="w-full bg-gray-100 border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Administrador"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-500 mb-1">Email del Admin</label>
                <input
                  type="email"
                  value={formData.admin_email}
                  onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
                  className="w-full bg-gray-100 border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="admin@empresa.com"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-500 mb-1">Contraseña del Admin</label>
                <input
                  type="password"
                  value={formData.admin_password}
                  onChange={(e) => setFormData({ ...formData, admin_password: e.target.value })}
                  className="w-full bg-gray-100 border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="••••••••••••"
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm text-gray-500 mb-1">Notas</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
              className="w-full bg-gray-100 border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Notas internas sobre este tenant..."
            />
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-lg transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !formData.name || !formData.slug}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center gap-2"
          >
            {loading && (
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            )}
            {loading ? 'Creando...' : 'Crear Tenant'}
          </button>
        </div>
      </div>
    </div>
  );
}
